import { useState, useCallback, useEffect, useRef } from "react";

interface RateLimitState {
  count: number;
  lockedUntil: number | null;
}

interface LockoutDuration {
  threshold: number;
  durationMs: number;
}

const DEFAULT_DURATIONS: LockoutDuration[] = [
  { threshold: 5, durationMs: 30_000 },
  { threshold: 10, durationMs: 300_000 },
  { threshold: 15, durationMs: 900_000 },
];

function loadState(key: string): RateLimitState {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored) as RateLimitState;
      if (
        typeof parsed.count === "number" &&
        (parsed.lockedUntil === null ||
          typeof parsed.lockedUntil === "number")
      ) {
        return parsed;
      }
    }
  } catch {}
  return { count: 0, lockedUntil: null };
}

function saveState(key: string, state: RateLimitState) {
  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch {}
}

function getDuration(count: number, durations: LockoutDuration[]): number {
  let duration = 0;
  for (const d of durations) {
    if (count >= d.threshold) {
      duration = d.durationMs;
    }
  }
  return duration;
}

export default function useRateLimiter(
  storageKey: string,
  durations: LockoutDuration[] = DEFAULT_DURATIONS
) {
  const [state, setState] = useState<RateLimitState>(() =>
    loadState(storageKey)
  );
  const [remaining, setRemaining] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isLocked =
    state.lockedUntil !== null && Date.now() < state.lockedUntil;

  useEffect(() => {
    if (isLocked && state.lockedUntil) {
      const tick = () => {
        const rem = Math.max(
          0,
          Math.ceil((state.lockedUntil! - Date.now()) / 1000)
        );
        setRemaining(rem);
        if (rem <= 0) {
          setState(loadState(storageKey));
        }
      };
      tick();
      timerRef.current = setInterval(tick, 1000);
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    } else {
      setRemaining(0);
    }
  }, [isLocked, state.lockedUntil, storageKey]);

  const recordFailure = useCallback(() => {
    const current = loadState(storageKey);
    const newCount = current.count + 1;
    const lockoutDuration = getDuration(newCount, durations);
    const newState: RateLimitState = {
      count: newCount,
      lockedUntil:
        lockoutDuration > 0 ? Date.now() + lockoutDuration : null,
    };
    saveState(storageKey, newState);
    setState(newState);
  }, [storageKey, durations]);

  const recordSuccess = useCallback(() => {
    const newState: RateLimitState = { count: 0, lockedUntil: null };
    saveState(storageKey, newState);
    setState(newState);
  }, [storageKey]);

  return {
    isLocked,
    attemptCount: state.count,
    remainingLockout: remaining,
    recordFailure,
    recordSuccess,
  };
}
